/**
 * Persistent save schema. Plain JSON, versioned, with a migration chain and strict sanitising so a
 * hand-edited or half-written save can never crash the game: anything unusable falls back to defaults.
 */
import { emptyArsenal, type SavedArsenal } from '../weapons/arsenal';
import { emptyActivities, sanitizeActivities, type ActivitiesSave } from '../activities/state';
import type { JobRecord, MedalTier } from '../missions/medals';
import { emptyMayhem } from '../activities/mayhem';
import { RUNG_POINTS, SOURCES, emptyContacts, sanitizeContacts, type ContactTrack, type ContactsState, type SourceKey } from '../../shared/contacts';
import { BORROWED_TIME, GULLET_JOB, LAST_ORDERS, ONE_ARMED_BANDITS, THE_COLLECTION, WET_PAPER } from '../missions/ids';
import { emptyTrade, sanitizeTrade, type TradeSave } from '../trade/tradeModel';

export const SAVE_VERSION = 9;
export const SAVE_KEY = 'borough-heat.save';

/** 'auto' lets the render module pick (and adapt) the tier from the frame rate. */
export type GraphicsQuality = 'auto' | 'low' | 'medium' | 'high';

export interface GameSettings {
  /** 0..1 */
  master: number;
  music: number;
  sfx: number;
  graphics: GraphicsQuality;
  /** 0..1 multiplier on camera shake. */
  cameraShake: number;
  /** Follow-camera distance multiplier, 0.55 (close) .. 1.2 (far). */
  cameraZoom: number;
  /** Last tuned radio station (-1 = off). New saves start on station 0, so the first car has music. */
  radioStation: number;
  /** On-screen touch controls (docs/MOBILE.md): size multiplier 0.7..1.4. */
  touchSize: number;
  /** Touch controls opacity 0.2..1. */
  touchOpacity: number;
  /** Mirror the touch layout: stick on the right, buttons on the left. */
  touchLeftHanded: boolean;
  /** Short vibrations on button presses and hard knocks (where the browser supports them). */
  touchHaptics: boolean;
  /** Steering sensitivity 0.5..1.5 (docs/DRIVING.md §8): how quickly the wheel answers keys, pad and stick. */
  steerSensitivity: number;
  /** Driving assists: speed-sensitive keys; on touch the road alignment and hold and the stuck assist. */
  driveAssists: boolean;
  /** Route arrows painted on the road to the objective / the place marked on the map (Settings → Help). */
  routeArrows: boolean;
  // (A `playerModel` setting existed briefly while the detailed figures were a test; old saves still
  // carry it and sanitizeSave drops it: the detailed figure is the only look now.)
}

export interface SaveStats {
  /** Seconds of simulated play time. */
  playTime: number;
  missionsCompleted: number;
  moneyEarned: number;
  moneySpent: number;
  timesBusted: number;
  timesWasted: number;
  carsStolen: number;
  /** Halloran export orders delivered (also picks the next order). */
  exportsDone: number;
  /** Phone-box street jobs done (also picks the next one). */
  streetJobsDone: number;
  /**
   * Highest wanted level the police gave up on ("Your record", the best getaway). Added after v8 without a
   * version bump: an older save simply has none yet (sanitising defaults it to 0).
   */
  bestEscape: number;
  /**
   * Metres driven at the wheel (the profile page's "Miles driven", src/profile). Added after v9 without a
   * version bump, as `bestEscape`: an older save simply starts at 0.
   */
  distanceDriven: number;
}

export interface SaveData {
  version: number;
  money: number;
  completedMissions: string[];
  /** Unlock ids, e.g. 'lockup-car', 'garage-tier-2'. */
  unlocks: string[];
  settings: GameSettings;
  stats: SaveStats;
  /** Weapons carried and their rounds (src/weapons). Unknown ids are dropped by the weapons module on load. */
  arsenal: SavedArsenal;
  /** Offline side activities: race and minicab tiers + best scores, matchbooks, the motor's build (src/activities). */
  activities: ActivitiesSave;
  /** Story-job stamps and best times by mission id (src/missions/medals.ts; docs/MISSIONS.md §Replays). */
  jobRecords: Record<string, JobRecord>;
  /** Standing with the contacts (WP-X1, shared/contacts.ts; src/contacts/contactsSystem.ts). */
  contacts: ContactsState;
  /**
   * Wilf's book, the offline trade round (src/trade, docs/TRADE-OFFLINE.md): goods in your pockets and the
   * stash, what you paid, Lenny's tips. Added after v8 without a version bump: an older save has none yet and
   * sanitising gives it an empty one (as `stats.bestEscape`).
   */
  trade: TradeSave;
  /** Epoch ms of the last write (informational — never used by simulation). */
  savedAt: number;
}

export const STARTING_MONEY = 40;

export function defaultSettings(): GameSettings {
  return { master: 0.8, music: 0.6, sfx: 0.9, graphics: 'auto', cameraShake: 1, cameraZoom: 0.85, radioStation: 0, touchSize: 1, touchOpacity: 0.75, touchLeftHanded: false, touchHaptics: true, steerSensitivity: 1, driveAssists: true, routeArrows: true };
}

export function defaultStats(): SaveStats {
  return { playTime: 0, missionsCompleted: 0, moneyEarned: 0, moneySpent: 0, timesBusted: 0, timesWasted: 0, carsStolen: 0, exportsDone: 0, streetJobsDone: 0, bestEscape: 0, distanceDriven: 0 };
}

export function defaultSave(): SaveData {
  return {
    version: SAVE_VERSION,
    money: STARTING_MONEY,
    completedMissions: [],
    unlocks: [],
    settings: defaultSettings(),
    stats: defaultStats(),
    arsenal: emptyArsenal(),
    activities: emptyActivities(),
    jobRecords: {},
    contacts: emptyContacts(),
    trade: emptyTrade(),
    savedAt: 0,
  };
}

/** True when the save holds any real progress (drives the title screen's CONTINUE button). */
export function hasProgress(s: SaveData): boolean {
  return s.completedMissions.length > 0 || s.unlocks.length > 0 || s.money !== STARTING_MONEY || s.stats.playTime > 5;
}

type Json = Record<string, unknown>;

const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown, fallback: number, lo = -Infinity, hi = Infinity): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback;
const strList = (v: unknown): string[] =>
  Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length < 80))].slice(0, 200) : [];

/**
 * Migration steps keyed by the version they upgrade FROM. Each receives and returns raw JSON.
 *   v0/v1 (prototype): { cash, done[], volume } -> v2 structure.
 *   v2 -> v3: graphics 'high' (old default) -> 'auto'.
 *   v3 -> v4: weapons arrived; older saves start with bare fists.
 *   v4 -> v5: offline side activities (races, minicab shifts, matchbooks, Halloran's workshop).
 *   v5 -> v6: job stamps and best times (replays from the book). Jobs finished before stamps existed show
 *             as DONE without a time (the book derives that from `completedMissions`).
 *   v6 -> v7: the mayhem challenges' record (best scores and stamps) inside `activities`.
 *   v7 -> v8: Pike's spray bay (src/garage) — the motor's build sheet gains matt paint, rim colour, neon
 *             ride height, camber and track width, all "as it came".
 *   v8 -> v9: contact ladders (WP-X1) — standing seeded from what the save already shows (`seedContacts`).
 */
const MIGRATIONS: Record<number, (raw: Json) => Json> = {
  0: (raw) => MIGRATIONS[1]!(raw),
  1: (raw) => {
    const out: Json = { ...raw };
    if (out.money === undefined && out.cash !== undefined) out.money = out.cash;
    if (out.completedMissions === undefined && out.done !== undefined) out.completedMissions = out.done;
    if (out.settings === undefined && typeof out.volume === 'number') out.settings = { master: out.volume };
    if (out.unlocks === undefined) out.unlocks = [];
    delete out.cash;
    delete out.done;
    delete out.volume;
    out.version = 2;
    return out;
  },
  // v2 -> v3: graphics gained 'auto' (the new default). 'high' was the old default and never reached the
  // render tier, so what those players actually had was auto; keep it that way.
  2: (raw) => {
    const out: Json = { ...raw };
    if (isObj(out.settings) && out.settings.graphics === 'high') out.settings = { ...out.settings, graphics: 'auto' };
    out.version = 3;
    return out;
  },
  // v3 -> v4: the arsenal. Nobody had a weapon before, so everyone starts with their fists.
  3: (raw) => {
    const out: Json = { ...raw };
    if (!isObj(out.arsenal)) out.arsenal = emptyArsenal();
    out.version = 4;
    return out;
  },
  // v4 -> v5: the side activities. Nothing raced, driven or found yet; Des has never seen your motor.
  4: (raw) => {
    const out: Json = { ...raw };
    if (!isObj(out.activities)) out.activities = emptyActivities();
    out.version = 5;
    return out;
  },
  // v5 -> v6: stamps arrived; nothing has been replayed or timed yet.
  5: (raw) => {
    const out: Json = { ...raw };
    if (!isObj(out.jobRecords)) out.jobRecords = {};
    out.version = 6;
    return out;
  },
  // v6 -> v7: the half-bricks (mayhem challenges). Nothing played yet.
  6: (raw) => {
    const out: Json = { ...raw };
    if (isObj(out.activities) && !isObj(out.activities.mayhem)) out.activities = { ...out.activities, mayhem: emptyMayhem() };
    out.version = 7;
    return out;
  },
  // v7 -> v8: the spray bay. The motor (if any) keeps everything Des did and has nothing from Pike yet.
  7: (raw) => {
    const out: Json = { ...raw };
    const a = out.activities;
    if (isObj(a) && isObj(a.motor) && isObj(a.motor.build)) {
      out.activities = { ...a, motor: { ...a.motor, build: { matt: false, rim: null, neon: null, ride: 0, camber: 0, track: 0, ...a.motor.build } } };
    }
    out.version = 8;
    return out;
  },
  // v8 -> v9: the contact ladders. A save with progress starts with the standing its record already earns.
  8: (raw) => {
    const out: Json = { ...raw };
    if (!isObj(out.contacts)) out.contacts = seedContacts(out);
    out.version = 9;
    return out;
  },
};

const STORY: readonly string[] = [BORROWED_TIME, WET_PAPER, GULLET_JOB, ONE_ARMED_BANDITS, THE_COLLECTION, LAST_ORDERS];
/** Repeatable work done before the ladders seeds a track at most this far (rung 3); one-off firsts count in full. */
export const SEED_CAP = RUNG_POINTS[3]!;

/**
 * Standing for a save from before the ladders (WP-X1, docs/CONTACTS.md): the story jobs done, the exports and
 * street jobs counted in the stats, races and shifts run, matchbooks found, weapons owned. Nothing of a day's
 * cap is used (the work was done on other days); repeatable work is capped at rung 3 per track.
 */
export function seedContacts(raw: Json): ContactsState {
  const pts: Partial<Record<ContactTrack, number>> = {};
  const add = (source: SourceKey, n: number): void => {
    const s = SOURCES[source];
    const k = Math.max(0, Math.floor(Number.isFinite(n) ? n : 0));
    if (!k) return;
    pts[s.track] = (pts[s.track] ?? 0) + s.points * k;
  };
  const st = isObj(raw.stats) ? raw.stats : {};
  const acts = isObj(raw.activities) ? raw.activities : {};
  const race = isObj(acts.race) ? acts.race : {};
  const cab = isObj(acts.cab) ? acts.cab : {};
  const arsenal = isObj(raw.arsenal) ? raw.arsenal : {};
  add('export', num(st.exportsDone, 0, 0, 10_000));
  add('street', num(st.streetJobsDone, 0, 0, 10_000));
  const runs = num(race.runs, 0, 0, 10_000);
  const wins = Math.min(runs, num(race.wins, 0, 0, 10_000));
  add('race-win', wins);
  add('race', runs - wins);
  add('cab', num(cab.runs, 0, 0, 10_000));
  add('fence-buy', strList(arsenal.owned).filter((w) => w !== 'fists').length);
  for (const t of Object.keys(pts) as ContactTrack[]) pts[t] = Math.min(SEED_CAP, pts[t]!);
  // One-off firsts in full.
  const firsts: Partial<Record<ContactTrack, number>> = {};
  firsts.mags = strList(raw.completedMissions).filter((id) => STORY.includes(id)).length * SOURCES.story.points;
  firsts.lenny = (Array.isArray(acts.matchbooks) ? Math.min(50, acts.matchbooks.length) : 0) * SOURCES.matchbook.points;
  for (const [t, v] of Object.entries(firsts) as [ContactTrack, number][]) if (v > 0) pts[t] = (pts[t] ?? 0) + v;
  return sanitizeContacts({ pts, day: 0, today: {} });
}

/** Shape-only clean-up of the job records: known-looking ids, stamps 1..3, sane times. */
function sanitizeJobRecords(raw: unknown): Record<string, JobRecord> {
  const out: Record<string, JobRecord> = {};
  if (!isObj(raw)) return out;
  for (const [id, r] of Object.entries(raw).slice(0, 50)) {
    if (id.length === 0 || id.length >= 40 || !isObj(r)) continue;
    const medal = Math.round(num(r.medal, 0, 0, 3)) as MedalTier;
    if (medal === 0) continue;
    const best = typeof r.best === 'number' && Number.isFinite(r.best) && r.best > 0 ? Math.min(r.best, 360_000) : null;
    out[id] = { medal, best, runs: Math.round(num(r.runs, 1, 0, 1_000_000)) };
  }
  return out;
}

/** Shape-only clean-up of the saved arsenal (ids and counts are validated against the roster by src/weapons). */
function sanitizeArsenal(raw: unknown): SavedArsenal {
  const d = emptyArsenal();
  if (!isObj(raw)) return d;
  const counts = (v: unknown): Record<string, number> => {
    const out: Record<string, number> = {};
    if (!isObj(v)) return out;
    for (const [k, n] of Object.entries(v).slice(0, 20)) {
      if (k.length > 0 && k.length < 40) out[k] = Math.round(num(n, 0, 0, 999));
    }
    return out;
  };
  const owned = strList(raw.owned).slice(0, 20);
  if (!owned.includes('fists')) owned.unshift('fists');
  const current = typeof raw.current === 'string' && owned.includes(raw.current) ? raw.current : 'fists';
  return { owned, current, loaded: counts(raw.loaded), reserve: counts(raw.reserve) };
}

/**
 * Turns anything (parsed JSON, garbage, null) into a valid current-version SaveData.
 * `migrated` is true when an older version was upgraded; `valid` is false when the input was unusable
 * (and defaults were returned instead).
 */
export function migrateSave(raw: unknown): { data: SaveData; valid: boolean; migrated: boolean } {
  if (!isObj(raw)) return { data: defaultSave(), valid: false, migrated: false };
  let cur: Json = raw;
  let version = num(cur.version, 0, 0, 1000);
  // A save from the future: keep what we understand but don't pretend it is ours.
  let migrated = false;
  while (version < SAVE_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) break;
    cur = step(cur);
    version = num(cur.version, SAVE_VERSION);
    migrated = true;
  }
  return { data: sanitizeSave(cur), valid: true, migrated };
}

/** Clamp and default every field. Never throws. */
export function sanitizeSave(raw: Json): SaveData {
  const d = defaultSave();
  const s = isObj(raw.settings) ? raw.settings : {};
  const st = isObj(raw.stats) ? raw.stats : {};
  const graphics = s.graphics === 'auto' || s.graphics === 'low' || s.graphics === 'medium' || s.graphics === 'high' ? s.graphics : d.settings.graphics;
  return {
    version: SAVE_VERSION,
    money: Math.round(num(raw.money, d.money, 0, 99_999_999)),
    completedMissions: strList(raw.completedMissions),
    unlocks: strList(raw.unlocks),
    settings: {
      master: num(s.master, d.settings.master, 0, 1),
      music: num(s.music, d.settings.music, 0, 1),
      sfx: num(s.sfx, d.settings.sfx, 0, 1),
      graphics,
      cameraShake: num(s.cameraShake, d.settings.cameraShake, 0, 1),
      cameraZoom: num(s.cameraZoom, d.settings.cameraZoom, 0.55, 1.2),
      radioStation: Math.round(num(s.radioStation, d.settings.radioStation, -1, 8)),
      touchSize: num(s.touchSize, d.settings.touchSize, 0.7, 1.4),
      touchOpacity: num(s.touchOpacity, d.settings.touchOpacity, 0.2, 1),
      touchLeftHanded: typeof s.touchLeftHanded === 'boolean' ? s.touchLeftHanded : d.settings.touchLeftHanded,
      touchHaptics: typeof s.touchHaptics === 'boolean' ? s.touchHaptics : d.settings.touchHaptics,
      // (`touchSteer` — POINT / CLASSIC / WHEEL — was a setting until 2026-10-30; old saves still carry it and it is dropped here: there is one touch driving scheme now.)
      steerSensitivity: num(s.steerSensitivity, d.settings.steerSensitivity, 0.5, 1.5),
      driveAssists: typeof s.driveAssists === 'boolean' ? s.driveAssists : d.settings.driveAssists,
      routeArrows: typeof s.routeArrows === 'boolean' ? s.routeArrows : d.settings.routeArrows,
    },
    stats: {
      playTime: num(st.playTime, 0, 0),
      missionsCompleted: Math.round(num(st.missionsCompleted, 0, 0)),
      moneyEarned: Math.round(num(st.moneyEarned, 0, 0)),
      moneySpent: Math.round(num(st.moneySpent, 0, 0)),
      timesBusted: Math.round(num(st.timesBusted, 0, 0)),
      timesWasted: Math.round(num(st.timesWasted, 0, 0)),
      carsStolen: Math.round(num(st.carsStolen, 0, 0)),
      exportsDone: Math.round(num(st.exportsDone, 0, 0)),
      streetJobsDone: Math.round(num(st.streetJobsDone, 0, 0)),
      bestEscape: Math.round(num(st.bestEscape, 0, 0, 5)),
      distanceDriven: Math.round(num(st.distanceDriven, 0, 0, 1e9)),
    },
    arsenal: sanitizeArsenal(raw.arsenal),
    activities: sanitizeActivities(raw.activities),
    jobRecords: sanitizeJobRecords(raw.jobRecords),
    contacts: sanitizeContacts(raw.contacts),
    trade: sanitizeTrade(raw.trade),
    savedAt: num(raw.savedAt, 0, 0),
  };
}
